import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, ExternalLink, Gem, MessageCircle, Send } from "lucide-react";
import { api, formatApiErrorDetail, inr } from "@/lib/api";
import { whatsappLink } from "@/lib/site";
import Reveal from "@/components/Reveal";

const MODELS = [
  {
    img: "https://static.prod-images.emergentagent.com/jobs/6b97e868-50f8-49a5-b8db-59f3ebd74a72/images/381362850a5c684a24b2a2c23ede9ce801640ff193006da75a91f825a8eea220.jpeg",
    label: "Antique Haram Look",
  },
  {
    img: "https://static.prod-images.emergentagent.com/jobs/6b97e868-50f8-49a5-b8db-59f3ebd74a72/images/533cb321b9bc0ae410321a0de0d1169eeb031bd8bb50d009f615c60efdd67ccd.jpeg",
    label: "Kundan Choker Look",
  },
];

const ACCESSORIES = [
  { key: "Necklaces", label: "Necklaces", match: (p) => p.category === "Necklaces" },
  { key: "Long Harams", label: "Long Harams", match: (p) => p.category === "Harams" },
  { key: "Chokers", label: "Chokers", match: (p) => p.category === "Chokers" },
  { key: "Earrings & Jhumkas", label: "Earrings & Jhumkas", match: (p) => /jhumka|earring|chandbali|stud/i.test(p.name) },
  { key: "Bangles & Bracelets", label: "Bangles & Bracelets", match: (p) => /bangle|bracelet/i.test(p.name) },
  { key: "Maang Tikka", label: "Maang Tikka", match: (p) => /tikka|maang/i.test(p.name) },
  { key: "Waist Belts (Oddiyanam)", label: "Waist Belts (Oddiyanam)", match: (p) => /belt|oddiyanam|vaddanam/i.test(p.name) },
  { key: "Jewellery Sets", label: "Jewellery Sets", match: (p) => /with earrings/i.test(p.name) },
];

const STYLES = ["Traditional", "Antique", "Temple", "Contemporary"];
const BUDGETS = ["Under ₹1,000", "₹1,000 – ₹2,500", "₹2,500 – ₹5,000", "Above ₹5,000"];
const OCCASIONS = ["Wedding", "Engagement", "Festival", "Gift", "Other"];

function AccessoryPicker({ selected, onSelect }) {
  const [acc, setAcc] = useState("Necklaces");
  const [products, setProducts] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api
      .get("/products")
      .then((d) => { setProducts(d.data); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);

  const active = ACCESSORIES.find((a) => a.key === acc);
  const matches = useMemo(() => products.filter(active.match).slice(0, 6), [products, active]);

  return (
    <div className="card-surface p-5 sm:p-6" data-testid="atelier-accessory-panel">
      <p className="overline-tag font-semibold">1 · Pick Your Accessory</p>
      <div className="mt-4 flex flex-wrap gap-2">
        {ACCESSORIES.map((a) => (
          <button
            key={a.key}
            onClick={() => setAcc(a.key)}
            className={`border px-3.5 py-2 font-cinzel text-[9px] uppercase transition-all duration-300 ${
              acc === a.key ? "border-gold bg-gold text-[#241A08]" : "border-line text-ink2 hover:border-gold hover:text-gold"
            }`}
            style={{ letterSpacing: "0.16em" }}
            data-testid={`atelier-acc-${a.key.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          >
            {a.label}
          </button>
        ))}
      </div>

      <div className="mt-5">
        {loaded && matches.length === 0 ? (
          <div className="border border-dashed border-gold/40 bg-bg2 p-6 text-center" data-testid="atelier-empty-category">
            <Gem size={20} className="mx-auto text-gold" />
            <p className="mt-2 font-display text-xl text-ink">Commissioned to order</p>
            <p className="mt-1 text-xs text-ink2">This accessory is made on commission — tell us your dream design below.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {matches.map((p) => (
              <div key={p._id} className={`border bg-surface p-2 transition-colors ${selected === p._id ? "border-gold" : "border-line"}`} data-testid={`atelier-item-${p._id}`}>
                <button onClick={() => onSelect(p)} className="block w-full text-left">
                  <div className="aspect-square w-full overflow-hidden bg-bg2">
                    <img src={p.images[0]} alt={p.name} loading="lazy" className="h-full w-full object-contain" />
                  </div>
                  <p className="mt-1.5 truncate font-display text-sm font-semibold text-ink">{p.name}</p>
                  <p className="font-mono text-[10px] text-ink2">{inr(p.price)}</p>
                </button>
                <Link to={`/product/${p._id}`} className="mt-1 inline-flex items-center gap-1 font-cinzel text-[8px] uppercase text-gold" style={{ letterSpacing: "0.16em" }}>
                  View <ExternalLink size={9} />
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function CustomAtelier() {
  const [model, setModel] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [form, setForm] = useState({
    name: "", phone: "", email: "", jewellery_type: "Necklaces", style: "Antique",
    colours: "", budget: BUDGETS[1], occasion: "Wedding", message: "",
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [composite, setComposite] = useState(null);
  const [styling, setStyling] = useState(false);
  const [styleFailed, setStyleFailed] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const onSelect = (p) => {
    setSelectedProduct(p);
    setForm((f) => ({ ...f, jewellery_type: p.category }));
    toast.success(`Added "${p.name}" — now shown on every look.`);
    if (window.innerWidth < 1024) {
      document.getElementById("atelier-look-panel")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append("reference", file);
      await api.post("/custom-requests", fd);
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const waText = `Hello Sashti Jewels! Custom design request:
• Type: ${form.jewellery_type}
• Style: ${form.style}
• Colours: ${form.colours || "—"}
• Budget: ${form.budget}
• Occasion: ${form.occasion}
${form.message ? `• Notes: ${form.message}` : ""}`;

  const showImgs = MODELS.map((m) => m.img);
  const showLabel = selectedProduct ? selectedProduct.name : MODELS[model].label;
  const styled = selectedProduct && composite && !styleFailed;
  const showSrc = styled ? composite : selectedProduct ? selectedProduct.images[0] : showImgs[model];
  const showFit = styled ? "object-cover" : selectedProduct ? "object-contain" : "object-cover";

  useEffect(() => {
    if (!selectedProduct) {
      setComposite(null);
      setStyling(false);
      setStyleFailed(false);
      return;
    }
    let alive = true;
    setStyling(true);
    setComposite(null);
    setStyleFailed(false);
    api
      .get("/atelier-visual", { params: { product_id: selectedProduct._id, look: model } })
      .then((d) => { if (alive) setComposite(d.data.url); })
      .catch(() => { if (alive) setStyleFailed(true); })
      .finally(() => { if (alive) setStyling(false); });
    return () => { alive = false; };
  }, [selectedProduct, model]);

  return (
    <section id="atelier" className="bg-[#F5EFE2] border-y border-gold/20" data-testid="custom-atelier">
      <div className="mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-16 py-20 lg:py-28">
        <Reveal>
          <div className="text-center">
            <p className="overline-tag font-semibold">Custom Atelier</p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl font-semibold text-ink">Design your own heirloom</h2>
            <p className="mx-auto mt-4 max-w-2xl text-sm text-ink2 leading-relaxed">
              Explore the looks, pick the accessory, and tell us your dream — our master artisans
              will craft it in antique finish and quote you personally.
            </p>
          </div>
        </Reveal>

        <div className="mt-12 grid gap-8 lg:grid-cols-[0.9fr_1.1fr]">
          <Reveal delay={0.08}>
            <div className="card-surface p-5 sm:p-6 scroll-mt-24" id="atelier-look-panel" data-testid="atelier-model-showcase">
              <div className="flex items-center justify-between gap-3">
                <p className="overline-tag font-semibold">2 · Choose Your Look</p>
                {selectedProduct && (
                  <button
                    onClick={() => { setSelectedProduct(null); setModel(0); }}
                    className="font-cinzel text-[9px] uppercase text-gold hover:underline"
                    style={{ letterSpacing: "0.16em" }}
                    data-testid="atelier-clear-selection"
                  >
                    Clear selection
                  </button>
                )}
              </div>
              <div className="relative mt-4 overflow-hidden border border-line bg-bg2">
                <img
                  src={showSrc}
                  alt={showLabel}
                  className={`aspect-[3/4] w-full ${showFit}`}
                  data-testid="atelier-model-image"
                />
                {styling && (
                  <div className="absolute inset-0 z-10 grid place-items-center bg-[#14100A]/50 p-4 text-center backdrop-blur-sm" data-testid="atelier-styling-overlay">
                    <div>
                      <span className="mx-auto block h-8 w-8 animate-spin rounded-full border-2 border-gold border-t-transparent" />
                      <p className="mt-3 font-cinzel text-[10px] uppercase text-[#F7F4EE]" style={{ letterSpacing: "0.18em" }}>Styling your piece on this look…</p>
                      <p className="mt-1 text-[10px] text-[#F7F4EE]/80">First view takes up to a minute — it is saved afterwards.</p>
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-4 flex flex-wrap gap-3">
                {showImgs.map((src, i) => (
                  <button
                    key={src}
                    onClick={() => setModel(i)}
                    className={`relative h-20 w-16 overflow-hidden border transition-all ${model === i ? "border-gold ring-1 ring-gold" : "border-line opacity-70 hover:opacity-100"}`}
                    data-testid={`atelier-model-${i}`}
                    aria-label={`${MODELS[i].label} — look ${i + 1}`}
                  >
                    <img src={src} alt="" className="h-full w-full object-cover" />
                    {model === i && (
                      <span className="absolute right-0.5 top-0.5 grid h-4 w-4 place-items-center rounded-full bg-gold text-[#241A08]">
                        <Check size={10} />
                      </span>
                    )}
                  </button>
                ))}
              </div>
              <p className="mt-3 font-display italic text-lg text-ink2">{showLabel}</p>
              <p className="mt-1 text-xs text-ink3">
                {selectedProduct
                  ? styleFailed
                    ? "Couldn't style this piece on the model right now — showing the product photo instead."
                    : `AI-styled preview of your selection on ${MODELS[model].label}. First view takes up to a minute, then it is saved for everyone.`
                  : "Model styling reference — pick an accessory above and see it on this look."}
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.14}>
            <AccessoryPicker selected={selectedProduct?._id} onSelect={onSelect} />
          </Reveal>
        </div>

        <Reveal delay={0.18}>
          <div className="mt-8 card-surface p-6 sm:p-10" data-testid="atelier-custom-form">
            {done ? (
              <div className="py-8 text-center">
                <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-gold text-[#241A08]">
                  <Check size={28} />
                </span>
                <h3 className="mt-5 font-display text-3xl font-semibold">Request received</h3>
                <p className="mx-auto mt-3 max-w-md text-sm text-ink2">
                  Your custom design brief is with our concierge — expect a call or WhatsApp within
                  a day. Want a faster reply? Forward the same brief on WhatsApp.
                </p>
                <div className="mt-7 flex flex-wrap justify-center gap-3">
                  <a href={whatsappLink(waText)} target="_blank" rel="noopener noreferrer" className="btn-gold" data-testid="atelier-whatsapp-followup">
                    <MessageCircle size={14} /> Send on WhatsApp
                  </a>
                  <button
                    onClick={() => { setDone(false); setFile(null); setSelectedProduct(null); setModel(0); if (fileRef.current) fileRef.current.value = ""; }}
                    className="btn-ghost-gold"
                    data-testid="atelier-request-another"
                  >
                    Request Another
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="atelier-form">
                <p className="overline-tag font-semibold">3 · Your Custom Design</p>
                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  <label className="block">
                    <span className="font-cinzel text-[10px] uppercase text-ink2" style={{ letterSpacing: "0.18em" }}>Jewellery type *</span>
                    <input required className="field mt-1.5" value={form.jewellery_type} onChange={set("jewellery_type")} data-testid="atelier-type" />
                  </label>
                  <label className="block">
                    <span className="font-cinzel text-[10px] uppercase text-ink2" style={{ letterSpacing: "0.18em" }}>Style</span>
                    <select className="field mt-1.5" value={form.style} onChange={set("style")} data-testid="atelier-style">
                      {STYLES.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </label>
                  <label className="block">
                    <span className="font-cinzel text-[10px] uppercase text-ink2" style={{ letterSpacing: "0.18em" }}>Preferred colours & stones</span>
                    <input className="field mt-1.5" placeholder="e.g. green kemp, pearl drops" value={form.colours} onChange={set("colours")} data-testid="atelier-colours" />
                  </label>
                  <label className="block">
                    <span className="font-cinzel text-[10px] uppercase text-ink2" style={{ letterSpacing: "0.18em" }}>Budget range</span>
                    <select className="field mt-1.5" value={form.budget} onChange={set("budget")} data-testid="atelier-budget">
                      {BUDGETS.map((b) => <option key={b}>{b}</option>)}
                    </select>
                  </label>
                  <label className="block">
                    <span className="font-cinzel text-[10px] uppercase text-ink2" style={{ letterSpacing: "0.18em" }}>Occasion</span>
                    <select className="field mt-1.5" value={form.occasion} onChange={set("occasion")} data-testid="atelier-occasion">
                      {OCCASIONS.map((o) => <option key={o}>{o}</option>)}
                    </select>
                  </label>
                  <label className="block">
                    <span className="font-cinzel text-[10px] uppercase text-ink2" style={{ letterSpacing: "0.18em" }}>Reference image (optional)</span>
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => setFile(e.target.files?.[0] || null)}
                      className="field mt-1.5 text-xs file:mr-3 file:border-0 file:bg-gold file:px-3 file:py-1 file:text-[10px] file:uppercase file:text-[#241A08] file:cursor-pointer"
                      data-testid="atelier-reference"
                    />
                  </label>
                  <label className="sm:col-span-2 block">
                    <span className="font-cinzel text-[10px] uppercase text-ink2" style={{ letterSpacing: "0.18em" }}>Design instructions</span>
                    <textarea rows={3} className="field mt-1.5 resize-none" placeholder="Length, pendant size, motifs you love…" value={form.message} onChange={set("message")} data-testid="atelier-message" />
                  </label>
                  <input required className="field" placeholder="Your name *" value={form.name} onChange={set("name")} data-testid="atelier-name" />
                  <input required className="field" placeholder="Phone / WhatsApp *" value={form.phone} onChange={set("phone")} data-testid="atelier-phone" />
                  <input type="email" className="field sm:col-span-2" placeholder="Email (optional)" value={form.email} onChange={set("email")} data-testid="atelier-email" />
                </div>
                <button type="submit" className="btn-gold mt-6 w-full justify-center" disabled={busy} data-testid="atelier-submit">
                  <Send size={14} /> {busy ? "Sending…" : "Request a Custom Design"}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
